import { useTranslation } from '@govard/i18n-core/react';
import { useState, type ReactElement } from 'react';
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  NotebookPen,
} from 'lucide-react';
import { Button, Checkbox } from 'antd';
import type { StructuredMessagePayload } from '@govard/ai-chat-kit';

export type MeetingOutcomeData = Extract<
  StructuredMessagePayload,
  { type: 'meeting_outcome' }
>['meeting'];

export interface MeetingTaskCandidate {
  title: string;
  rationale?: string | null;
}

interface MeetingOutcomeSnippetProps {
  meeting: MeetingOutcomeData;
  onCreateTasks?:
    | ((meeting: MeetingOutcomeData, actions: readonly MeetingTaskCandidate[]) => void)
    | undefined;
  createTasksDisabled?: boolean;
  onPrepareFollowUp?: ((meeting: MeetingOutcomeData) => void) | undefined;
  prepareFollowUpDisabled?: boolean;
}

export function MeetingOutcomeSnippet({
  meeting,
  onCreateTasks,
  createTasksDisabled = false,
  onPrepareFollowUp,
  prepareFollowUpDisabled = false,
}: MeetingOutcomeSnippetProps): ReactElement {
  const { t, locale } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(true);
  const nextActions = meeting.nextActions ?? [];
  const [selectedIndices, setSelectedIndices] = useState<ReadonlySet<number>>(() => {
    if (meeting.status !== 'ready') return new Set<number>();
    return new Set(nextActions.map((_, index) => index));
  });

  const toggleAction = (index: number): void => {
    setSelectedIndices((current) => {
      const next = new Set(current);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const formatNoteDate = (value: string): string =>
    new Date(value).toLocaleDateString(locale === 'ru' ? 'ru-RU' : 'en-US', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });

  const decisions = meeting.decisions ?? [];
  const risks = meeting.risks ?? [];
  const selectedActions = nextActions.filter((_, index) => selectedIndices.has(index));

  return (
    <div
      data-testid="structured-snippet-meeting-outcome"
      className="my-2 overflow-hidden rounded-xl border border-zinc-200/80 bg-white shadow-2xs transition-all hover:border-zinc-300"
    >
      <div
        onClick={() => setIsExpanded((prev) => !prev)}
        className="flex cursor-pointer items-center justify-between gap-3 px-3.5 py-2.5 text-xs hover:bg-zinc-50/70 select-none border-b border-zinc-100"
      >
        <div className="flex min-w-0 items-center gap-2">
          <NotebookPen size={14} className="text-zinc-600" />
          <span className="font-semibold text-zinc-900">{t('crm.chat.meeting_outcome.title')}</span>
          <span className="min-w-0 truncate text-[11px] font-normal text-zinc-500">
            {meeting.deal.title}
          </span>
        </div>

        <button
          type="button"
          className="flex items-center gap-1 text-[11px] font-medium text-zinc-500 hover:text-zinc-800"
        >
          <span>
            {isExpanded ? t('crm.ai_chat.widget.collapse') : t('crm.ai_chat.widget.expand')}
          </span>
          {isExpanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
        </button>
      </div>

      {isExpanded && (
        <div className="text-xs">
          {meeting.status === 'no_note' ? (
            <div className="p-3.5">
              <p data-testid="meeting-outcome-empty-state" className="text-zinc-600">
                {meeting.reason === 'empty'
                  ? t('crm.chat.meeting_outcome.no_note_empty')
                  : t('crm.chat.meeting_outcome.no_note_missing')}
              </p>
            </div>
          ) : meeting.status === 'too_long' ? (
            <div className="p-3.5">
              <p data-testid="meeting-outcome-too-long-state" className="text-zinc-600">
                {t('crm.chat.meeting_outcome.note_too_long')}
              </p>
            </div>
          ) : meeting.status === 'analysis_unavailable' ? (
            <div className="space-y-2 p-3.5">
              <p data-testid="meeting-outcome-unavailable-state" className="text-zinc-600">
                {t('crm.chat.meeting_outcome.analysis_unavailable')}
              </p>
              {meeting.note && (
                <p data-testid="meeting-outcome-source" className="text-[11px] text-zinc-400">
                  {t('crm.chat.meeting_outcome.source_latest_note')} ·{' '}
                  {meeting.note.authorName ?? meeting.note.id} ·{' '}
                  {formatNoteDate(meeting.note.createdAt)}
                </p>
              )}
            </div>
          ) : (
            <div className="space-y-3 p-3.5">
              {meeting.note && (
                <p data-testid="meeting-outcome-source" className="text-[11px] text-zinc-400">
                  {t('crm.chat.meeting_outcome.source_latest_note')} ·{' '}
                  {meeting.note.authorName ?? meeting.note.id} ·{' '}
                  {formatNoteDate(meeting.note.createdAt)}
                </p>
              )}

              <section data-testid="meeting-outcome-summary">
                <p className="mb-1 flex items-center gap-1.5 font-medium text-zinc-800">
                  <NotebookPen size={12} className="text-zinc-500" />
                  {t('crm.chat.meeting_outcome.summary')}
                </p>
                <p className="text-zinc-700">{meeting.summary}</p>
              </section>

              <section data-testid="meeting-outcome-decisions">
                <p className="mb-1 flex items-center gap-1.5 font-medium text-zinc-800">
                  <CheckCircle2 size={12} className="text-emerald-600" />
                  {t('crm.chat.meeting_outcome.decisions')}
                </p>
                {decisions.length > 0 ? (
                  <ul className="space-y-1">
                    {decisions.map((item) => (
                      <li key={item} className="text-zinc-700">
                        {item}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-zinc-400 italic">{t('crm.chat.meeting_outcome.empty_list')}</p>
                )}
              </section>

              <section data-testid="meeting-outcome-risks">
                <p className="mb-1 flex items-center gap-1.5 font-medium text-zinc-800">
                  <AlertTriangle size={12} className="text-orange-600" />
                  {t('crm.chat.meeting_outcome.risks')}
                </p>
                {risks.length > 0 ? (
                  <ul className="space-y-1">
                    {risks.map((item) => (
                      <li key={item} className="text-zinc-700">
                        {item}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-zinc-400 italic">{t('crm.chat.meeting_outcome.empty_list')}</p>
                )}
              </section>

              <section data-testid="meeting-outcome-next-actions">
                <p className="mb-1 flex items-center gap-1.5 font-medium text-zinc-800">
                  <ArrowRight size={12} className="text-blue-600" />
                  {t('crm.chat.meeting_outcome.next_actions')}
                </p>
                {nextActions.length > 0 ? (
                  onCreateTasks === undefined ? (
                    <ol className="space-y-1.5">
                      {nextActions.map((action) => (
                        <li key={action.title} className="text-zinc-700">
                          <span className="font-medium">{action.title}</span>
                          {action.rationale && (
                            <span className="block text-[11px] text-zinc-400">
                              {action.rationale}
                            </span>
                          )}
                        </li>
                      ))}
                    </ol>
                  ) : (
                    <>
                      <p
                        data-testid="meeting-outcome-selected-count"
                        className="mb-2 text-zinc-500"
                      >
                        {t('crm.chat.meeting_outcome.selected_count', {
                          selected: selectedActions.length,
                          total: nextActions.length,
                        })}
                      </p>
                      <ol className="space-y-1.5">
                        {nextActions.map((action, index) => (
                          <li
                            key={`${action.title}-${index}`}
                            className="rounded-md border border-zinc-100 bg-zinc-50/60 p-2"
                          >
                            <Checkbox
                              checked={selectedIndices.has(index)}
                              onChange={() => toggleAction(index)}
                              aria-label={t('crm.chat.meeting_outcome.select_action', {
                                title: action.title,
                              })}
                              data-testid={`meeting-outcome-select-${index}`}
                            >
                              <span className="font-medium text-zinc-700">{action.title}</span>
                            </Checkbox>
                            {action.rationale && (
                              <span className="mt-0.5 block pl-6 text-[11px] text-zinc-400">
                                {action.rationale}
                              </span>
                            )}
                          </li>
                        ))}
                      </ol>
                    </>
                  )
                ) : (
                  <p className="text-zinc-400 italic">{t('crm.chat.meeting_outcome.empty_list')}</p>
                )}
              </section>

              {onCreateTasks !== undefined && (
                <Button
                  type="primary"
                  block
                  data-testid="meeting-outcome-create-tasks"
                  disabled={createTasksDisabled || selectedActions.length === 0}
                  onClick={() =>
                    onCreateTasks(
                      meeting,
                      selectedActions.map((action) => ({
                        title: action.title,
                        ...(action.rationale === undefined ? {} : { rationale: action.rationale }),
                      })),
                    )
                  }
                >
                  {t('crm.chat.meeting_outcome.create_selected_tasks')}
                </Button>
              )}

              {onPrepareFollowUp !== undefined && (
                <Button
                  block
                  data-testid="meeting-outcome-prepare-follow-up"
                  disabled={prepareFollowUpDisabled}
                  onClick={() => onPrepareFollowUp(meeting)}
                >
                  {t('crm.chat.meeting_outcome.prepare_follow_up')}
                </Button>
              )}

              {onCreateTasks !== undefined && (
                <p className="text-zinc-500">{t('crm.chat.meeting_outcome.approval_hint')}</p>
              )}

              <p className="rounded-md bg-zinc-50 p-2.5 text-[11px] text-zinc-500">
                {t('crm.chat.meeting_outcome.read_only_note')}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
